import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpenOutlined } from '@ant-design/icons';
import { Badge } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import AnalysisContext from '@totem/components/surveyV2/analysis/AnalysisContext';
import { AnalysisPolicy } from '@totem/components/surveyV2/analysis/types';
import { SurveyQueryResult } from '@totem/components/surveyV2/types';
import { LevelOfEffortToString } from '@totem/components/surveyV2/utilities/SurveyEnumerations';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import colors from '@totem/styles/colors';
import { NumbericFilterOption, StringFilterOption } from '@totem/types/common';
import { LevelOfEffort } from '@totem/types/levelOfEffort';
import { isNotNull } from '@totem/utilities/common';
import { stringifyArray } from '@totem/utilities/tableFilterUtilities';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

const OVERFLOW_COUNT = 9999;

const styles = {
  compliantBadge: {
    background: colors.antd.blue,
  },
  nonCompliantBadge: {
    background: colors.criticality.high,
  },
  link: {
    color: colors.antd.blue,
  },
};

const AnalysisTable = () => {
  const { data, filteredData, loading, input, setInput } =
    useContext(AnalysisContext);
  const [policyNameFilterOptions, setPolicyNameFilterOptions] = useState<
    StringFilterOption[]
  >([]);
  const [categoryFilterOptions, setCategoryFilterOptions] = useState<
    StringFilterOption[]
  >([]);
  const [nistFilterOptions, setNistFilterOptions] = useState<
    StringFilterOption[]
  >([]);
  const [levelOfEffortFilterOptions, setLevelOfEffortFilterOptions] = useState<
    NumbericFilterOption[]
  >([]);

  const getData = () => {
    if (isNotNull(filteredData)) {
      return filteredData;
    }
    return [];
  };

  useEffect(() => {
    if (isNotNull(data)) {
      const optionsPolicyName = [];
      const optionsCategories = [];
      const optionsNist = [];
      const optionsLevelOfEffort = [];
      for (let idx = 0; idx < data.auditPolicies.length; idx++) {
        if (
          optionsPolicyName.findIndex(
            (chk) => chk.text === data.auditPolicies[idx].policyName,
          ) === -1
        ) {
          optionsPolicyName.push({
            text: data.auditPolicies[idx].policyName,
            value: data.auditPolicies[idx].policyName,
          });
        }
        if (
          optionsCategories.findIndex(
            (chk) => chk.text === data.auditPolicies[idx].category,
          ) === -1
        ) {
          optionsCategories.push({
            text: data.auditPolicies[idx].category,
            value: data.auditPolicies[idx].category,
          });
        }
        if (
          optionsNist.findIndex(
            (chk) => chk.text === data.auditPolicies[idx].nist,
          ) === -1
        ) {
          optionsNist.push({
            text: data.auditPolicies[idx].nist,
            value: data.auditPolicies[idx].nist,
          });
        }
        if (
          optionsLevelOfEffort.findIndex(
            (chk) => chk.value === data.auditPolicies[idx].levelOfEffort,
          ) === -1
        ) {
          optionsLevelOfEffort.push({
            text: LevelOfEffortToString(data.auditPolicies[idx].levelOfEffort),
            value: data.auditPolicies[idx].levelOfEffort,
          });
        }
      }
      setPolicyNameFilterOptions(
        optionsPolicyName.sort((compA, compB) =>
          sortStringAscending(compA.text, compB.text),
        ),
      );

      setCategoryFilterOptions(
        optionsCategories.sort((compA, compB) =>
          sortStringAscending(compA.text, compB.text),
        ),
      );

      setNistFilterOptions(
        optionsNist.sort((compA, compB) =>
          sortStringAscending(compA.text, compB.text),
        ),
      );

      setLevelOfEffortFilterOptions(
        optionsLevelOfEffort.sort((compA, compB) =>
          sortNumberAscending(compA.value, compB.value),
        ),
      );
    }
  }, [data]);

  const columns: ColumnProps<AnalysisPolicy>[] = [
    {
      title: 'Audit Policies',
      dataIndex: 'policy',
      key: 'policy',
      showSorterTooltip: true,
      render: (_, record: AnalysisPolicy) => (
        <Link to={`/dashboard/findings/policyaudits/v2/${record.questionId}`}>
          {record.policy}
        </Link>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.policy === compB.policy
          ? 0
          : compA.policy > compB.policy
            ? 1
            : -1,
    },
    {
      title: 'Policy Name',
      dataIndex: 'policyName',
      key: 'policyName',
      showSorterTooltip: true,
      render: (_, record: AnalysisPolicy) => record.policyName,
      defaultSortOrder: 'ascend',
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.policyName === compB.policyName
          ? 0
          : compA.policyName > compB.policyName
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: stringifyArray(input.policyName),
      filters: policyNameFilterOptions,
    },
    {
      title: 'Non-Compliant',
      dataIndex: 'noncompliant',

      sorter: (compA, compB) => compA.noncompliant - compB.noncompliant,
      render: (_, record: AnalysisPolicy) => (
        <Badge
          count={record.noncompliant}
          showZero
          overflowCount={OVERFLOW_COUNT}
          style={styles.nonCompliantBadge}
        />
      ),
    },
    {
      title: 'Compliant',
      dataIndex: 'compliant',

      sorter: (compA, compB) => compA.compliant - compB.compliant,
      render: (_, record: AnalysisPolicy) => (
        <Badge
          count={record.compliant}
          style={styles.compliantBadge}
          showZero
          overflowCount={OVERFLOW_COUNT}
        />
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      showSorterTooltip: true,
      render: (_, record: AnalysisPolicy) => record.category,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.category === compB.category
          ? 0
          : compA.category > compB.category
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: stringifyArray(input.category),
      filters: categoryFilterOptions,
    },
    {
      title: 'NIST',
      dataIndex: 'nist',
      key: 'nist',
      showSorterTooltip: true,
      render: (_, record: AnalysisPolicy) => record.nist,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.nist === compB.nist ? 0 : compA.nist > compB.nist ? 1 : -1,
      filterMultiple: true,
      filteredValue: stringifyArray(input.nist),
      filters: nistFilterOptions,
    },
    {
      title: 'Level of Effort',
      dataIndex: 'levelOfEffort',
      render: (value: number) => LevelOfEffort[value],
      sorter: (compA, compB) => compA.levelOfEffort - compB.levelOfEffort,
      filterMultiple: true,
      filteredValue: stringifyArray(input.levelOfEffort),
      filters: levelOfEffortFilterOptions,
    },
  ];

  const handleTableChange = (
    updatedPagination: TablePaginationConfig,
    filters: SorterResult<SurveyQueryResult>,
    sorter,
  ) => {
    const { ...params } = filters;

    let sortDir: string = sorter.order === 'descend' ? '-1' : '1';
    if (typeof sorter.order === 'undefined' || sorter.order === null) {
      if (
        typeof input.sortDirection !== 'undefined' &&
        input.sortDirection !== null
      ) {
        sortDir = input.sortDirection;
      }
    }

    let sortField = input.sortField ? input.sortField : 'lastOccurrence';
    if (
      typeof sorter.field !== 'undefined' &&
      typeof sorter.order !== 'undefined'
    ) {
      sortField = sorter.field;
    }

    // @ts-ignore
    setInput({
      ...input,
      ...params,
      pageSize: updatedPagination.pageSize,
      page: updatedPagination.current,
      sortField,
      sortDirection: sortDir,
    });
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getData()}
      loading={loading}
      rowKey={(record) => record.questionId}
      onChange={handleTableChange}
      pagination={{
        current: input.page,
        pageSize: input.pageSize,
        total: isNotNull(filteredData) ? filteredData.length : 0,
        showSizeChanger: true,
      }}
      expandable={{
        expandedRowRender: () => <span />,
        expandIcon: ({ record }) => (
          <Link
            to={`/dashboard/findings/policyaudits/v2/${record.questionId}`}
            state={{ fromPolicyAuditFindings: true }}
          >
            <FolderOpenOutlined
              style={{ cursor: 'pointer', color: colors.opacity.black0_8 }}
            />
          </Link>
        ),
      }}
    />
  );
};

export default AnalysisTable;
